import { ahuBoardApi } from 'app/services/ahuBoardApi';
import type { IMasterRow } from 'app/services/ahuComponentFormsApi';

/**
 * Phase 6 Vendor-Port W2 — GAS HEATER selector data layer (§33.5).
 *
 * Gas heater rating is SERVER-SIDE (a CMS catalog search + a model.save), NOT
 * an external vendor /rating/ dispatch (§32 is bypassed). There are FOUR write
 * families, ALL userArmed-gated by the selector (NEVER auto-fired — see
 * GasHeaterSelector.tsx data-safety + the prompt ★ DATA-SAFETY block):
 *
 *  1. RATE / APPLY (searchGasHeaters) — PATCH ahu/gas_heaters/<id>
 *     {gas_heater_params:JSON, eadb_temp, airflow_actual, air_stream_id,
 *      gas_inlet_pressure, qty_wide, qty_high, power_id, conn_hand_id,
 *      weather_rating_id, burner_manifold_pressure_conditional,
 *      ladb_temp_desired, assy_config_id?, fuel_type_id, firing_mode_id,
 *      gas_pilot_config_id, assy_height_from_floor, assy_distance_from_left_wall,
 *      master_gas_heater_id?, venting_id?, rate, isSelect}
 *     Legacy: self.model.save(…, {patch:true}) (abortable saveRequest).
 *     `rate:true`  = the search/preview write (Rate button, isSelect undefined);
 *     `rate:false` = the persisted Select write (isSelect:true). For type 1 the
 *     master_gas_heater_id is the chosen rated row; for type 3 (direct-fire) the
 *     server computes from the assy_config_id + burner POSTs (no master id).
 *
 *  2. BURNER POST (type-3 only) — POST ahu/gas_heaters/burner/
 *     {gas_heater_id, burner_id, rate?}. Legacy data_exchanger loop: with
 *     rate:true per burner config BEFORE the search (Rate); WITHOUT rate for the
 *     selected type-3 (Select). Smoke never reaches this (all dev3 test GH are
 *     type 1).
 *
 *  3. BURNER DELETE — the burner_model destroy (ahu/gas_heaters/burner/
 *     <gh_id>/<burner_id>/). Not in scope for the read-only smoke; the abortable
 *     re-search covers it. Wired here for completeness.
 *
 *  4. PRINT PDF (the ONLY celery flow) — POST /api/v1.0/rating/pdf_rating/
 *     {…collectDataForPDF}. Returns {task_id}; the report URL arrives over the
 *     socket `data` message (§33.5; guarded with REAL equality — [NEG]#79 fix —
 *     in the selector). Legacy: $.ajax POST then socket.on('data').
 *
 * READS (param chain, burner assy, count, catalog) reuse the shared
 * getMasterListByUrl GET (mapToCMS:false → the param GETs return plain arrays /
 * {count} objects). Only the WRITES + the count/catalog typed GETs live here.
 */

export type IGasHeaterRow = Record<string, unknown>;

/** A row of ahu/gas_heaters/burner/ (AHUGasHeaterBurnerSerializer). */
export interface IGasHeaterBurnerRow {
  id: number;
  gas_heater_id?: number;
  burner_id?: number;
  rate?: boolean;
  burner_config_id?: number | null;
}

/** RATE/APPLY payload (the searchGasHeaters model.save body — §33.5). */
export interface GhRatePayload {
  gas_heater_params: string; // JSON.stringify(params)
  eadb_temp?: unknown;
  airflow_actual?: unknown;
  air_stream_id?: unknown;
  gas_inlet_pressure?: unknown;
  qty_wide?: unknown;
  qty_high?: unknown;
  power_id?: unknown;
  conn_hand_id?: unknown;
  venting_id?: unknown;
  weather_rating_id?: unknown;
  burner_manifold_pressure_conditional?: unknown;
  ladb_temp_desired?: unknown;
  assy_config_id?: unknown;
  fuel_type_id?: unknown;
  firing_mode_id?: unknown;
  gas_pilot_config_id?: unknown;
  assy_height_from_floor?: unknown;
  assy_distance_from_left_wall?: unknown;
  master_gas_heater_id?: unknown;
  rate: boolean;
  isSelect?: boolean;
}

export const gasHeaterSelectorApi = ahuBoardApi.injectEndpoints({
  endpoints: (builder) => ({
    // --- 1. RATE / APPLY (server-side catalog search + persist) ----------
    // PATCH ahu/gas_heaters/<id>. The abortable saveRequest in legacy maps to
    // RTKQ's mutation .abort(); the selector keeps the promise and aborts a
    // superseded rate.
    rateGasHeater: builder.mutation<IGasHeaterRow, { id: number; data: GhRatePayload }>({
      query: ({ id, data }) => ({
        url: `/api/v1.0/ahu/gas_heaters/${id}`,
        method: 'PATCH',
        body: data,
      }),
    }),

    // --- BURNER LIST (type-3) --------------------------------------------
    // GET ahu/gas_heaters/burner/?gas_heater_id=<id> — legacy's burnerCollection
    // (collection/burner.js), fetched by showBurner() when the selected burner
    // assembly is User Defined (id 1), so the rows are the heater's own saved
    // burners rather than the assembly's fixed ones.
    getGasHeaterBurners: builder.query<IGasHeaterBurnerRow[], number>({
      query: (gas_heater_id) =>
        `/api/v1.0/ahu/gas_heaters/burner/?gas_heater_id=${gas_heater_id}`,
      transformResponse: (res: unknown): IGasHeaterBurnerRow[] =>
        Array.isArray(res) ? (res as IGasHeaterBurnerRow[]) : [],
    }),

    // --- 2. BURNER POST (type-3) -----------------------------------------
    postGasHeaterBurner: builder.mutation<
      Record<string, unknown>,
      { gas_heater_id: number; burner_id: number; rate?: boolean }
    >({
      query: ({ gas_heater_id, burner_id, rate }) => ({
        url: `/api/v1.0/ahu/gas_heaters/burner/`,
        method: 'POST',
        body: rate === undefined ? { gas_heater_id, burner_id } : { gas_heater_id, burner_id, rate },
      }),
    }),

    // --- 3. BURNER DELETE (type-3) ---------------------------------------
    deleteGasHeaterBurner: builder.mutation<
      unknown,
      { gas_heater_id: number; burner_id: number }
    >({
      query: ({ gas_heater_id, burner_id }) => ({
        url: `/api/v1.0/ahu/gas_heaters/burner/${gas_heater_id}/${burner_id}/`,
        method: 'DELETE',
      }),
    }),

    // --- 4. PRINT PDF (celery) -------------------------------------------
    // POST /api/v1.0/rating/pdf_rating/ → {task_id}. The selector then waits on
    // the socket `data` message for the generated report URL.
    requestGasHeaterPdf: builder.mutation<{ task_id: string }, Record<string, unknown>>({
      query: (data) => ({
        url: `/api/v1.0/rating/pdf_rating/`,
        method: 'POST',
        body: data,
      }),
    }),

    // --- typed availability count + catalog page (the search results) ----
    // count → {count} (legacy collectData); catalog → gas_heaters_list array
    // (legacy loadParams). Both mapToCMS:false plain GETs. URL-keyed RTKQ cache
    // replaces the stateStorage 'gas-heater-rating-dialog' URL cache.
    getGasHeaterCount: builder.query<number, string>({
      query: (url) => url,
      transformResponse: (res: unknown): number => {
        if (typeof res === 'number') return res;
        const o = res as { count?: number } | null;
        return Number(o?.count) || 0;
      },
      keepUnusedDataFor: 24 * 60 * 60,
    }),
    getGasHeaterCatalog: builder.query<IGasHeaterRow[], string>({
      query: (url) => url,
      transformResponse: (res: unknown): IGasHeaterRow[] =>
        Array.isArray(res) ? (res as IGasHeaterRow[]) : ((res as { results?: IGasHeaterRow[] })?.results ?? []),
      keepUnusedDataFor: 24 * 60 * 60,
    }),
    // Burner-assembly master list (legacy getBurnerAssy). Array of
    // {gh_burner_assy:{…}} rows. Reused via getMasterListByUrl in practice; the
    // typed variant is here for the type-3 builder.
    getGhBurnerAssy: builder.query<IMasterRow[], string>({
      query: (url) => url,
      transformResponse: (res: unknown): IMasterRow[] =>
        Array.isArray(res) ? (res as IMasterRow[]) : [],
      keepUnusedDataFor: 24 * 60 * 60,
    }),
  }),
  overrideExisting: false,
});

export const {
  useRateGasHeaterMutation,
  usePostGasHeaterBurnerMutation,
  useDeleteGasHeaterBurnerMutation,
  useRequestGasHeaterPdfMutation,
  useGetGasHeaterCountQuery,
  useGetGasHeaterCatalogQuery,
  // legacy loadParams runs on demand — on the initial render of an already
  // rated heater (js:1207) and again on Rate (js:1934) — never on every filter
  // change, so the selector drives it with the LAZY trigger rather than a
  // standing subscription.
  useLazyGetGasHeaterCatalogQuery,
  useGetGhBurnerAssyQuery,
  // renderBurner fetches the assembly list on demand, after the rate PATCH has
  // returned the burner_length_required_min it is scoped by (js:744-747), so
  // this is a lazy trigger rather than a standing subscription.
  useLazyGetGhBurnerAssyQuery,
  useLazyGetGasHeaterBurnersQuery,
} = gasHeaterSelectorApi;
